import React from 'react';

// Import workflow states styles
import './scss/WorkflowStates.scss';
import CircleCheckOutlined from '../icons/CircleCheckOutlined';

/**
 * Loading State Component
 * Shown while white label configuration is being loaded
 */
export const LoadingState = () => (
  <div className="workflow-state loading-state">
    <div className="loading-content">
      <div className="spinner"></div>
      <h2>Loading Configuration</h2>
      <p>Please wait while we load your white label configuration...</p>
    </div>
  </div>
);

/**
 * Error State Component
 * Shown when there's an error loading the configuration
 */
export const ErrorState = ({ error, onRetry }) => (
  <div className="workflow-state error-state">
    <div className="error-content">
      <div className="error-icon">
        <span className="material-symbols-outlined">error</span>
      </div>
      <h2>Error Loading Configuration</h2>
      <p className="error-message">{error}</p>
      <div className="error-actions">
        <button onClick={onRetry} className="btn btn-primary">
          <span className="material-symbols-outlined">refresh</span>
          Try Again
        </button>
      </div>
    </div>
  </div>
);

/**
 * Subscription Processing State Component
 * Shown during the subscription activation process
 */
export const SubscriptionProcessingState = () => (
  <div className="workflow-state processing-state">
    <div className="processing-content">
      <div className="processing-animation">
        <div className="loading-spinner"></div>
      </div>
      <h2>Setting Up Your White Label Instance</h2>
      <p>Please wait while we activate your subscription and prepare your white label instance...</p>

      <div className="processing-steps">
        <div className="step active">
          <span className="step-icon">
            <CircleCheckOutlined />
          </span>
          <span className="step-text">Payment processed</span>
        </div>
        <div className="step active">
          <span className="step-icon">
            <span className="material-symbols-outlined">sync</span>
          </span>
          <span className="step-text">Activating subscription</span>
        </div>
        <div className="step">
          <span className="step-icon">
            <span className="material-symbols-outlined">settings</span>
          </span>
          <span className="step-text">Preparing instance</span>
        </div>
      </div>

      <div className="processing-note">
        <p><strong>Note:</strong> This process may take a few moments. Please don't close this page.</p>
      </div>
    </div>
  </div>
);

/**
 * Generic Workflow State Container
 * Provides consistent styling for all workflow states
 */
export const WorkflowStateContainer = ({ children, className = '' }) => (
  <div className={`workflow-state-container ${className}`}>
    {children}
  </div>
);

/**
 * Status Badge Component
 * Used to display status information in various states
 */
export const StatusBadge = ({ status, label }) => {
  const getStatusClass = (status) => {
    switch (status) {
      case 'active':
        return 'status-active';
      case 'inactive':
        return 'status-inactive';
      case 'pending':
        return 'status-pending';
      case 'error':
        return 'status-error';
      default:
        return 'status-default';
    }
  };

  return (
    <span className={`status-badge ${getStatusClass(status)}`}>
      {label || status}
    </span>
  );
};

/**
 * Progress Indicator Component
 * Shows progress through the white label setup process
 */
export const ProgressIndicator = ({ currentStep, totalSteps, steps }) => {
  return (
    <div className="progress-indicator">
      <div className="progress-bar">
        <div
          className="progress-fill"
          style={{ width: `${(currentStep / totalSteps) * 100}%` }}
        ></div>
      </div>

      <div className="progress-steps">
        {steps.map((step, index) => (
          <div
            key={index}
            className={`progress-step ${index < currentStep ? 'completed' : ''} ${index === currentStep ? 'active' : ''}`}
          >
            <div className="step-indicator">
              {index < currentStep ? (
                <span className="material-symbols-outlined">check</span>
              ) : (
                <span>{index + 1}</span>
              )}
            </div>
            <span className="step-label">{step}</span>
          </div>
        ))}
      </div>
    </div>
  );
};

/**
 * Info Card Component
 * Displays informational content in workflow states
 */
export const InfoCard = ({ icon, title, description, action }) => (
  <div className="info-card">
    {icon && (
      <div className="info-card__icon">
        <span className="material-symbols-outlined">{icon}</span>
      </div>
    )}
    <div className="info-card__content">
      <h3>{title}</h3>
      <p>{description}</p>
      {action && (
        <div className="info-card__action">
          {action}
        </div>
      )}
    </div>
  </div>
);
